import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, FormControl, FormLabel, Input, Select, Spinner, Stack,
  Switch, Text, Textarea, useColorMode, useToast,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";
import "../Outreach/Outreach.css";

// Prompts for the OUTREACH auto-responder — the separate assistant that answers
// replies to platform drip campaigns. Not Ezra's prompts, not support's: this
// page edits outreach_prompts and nothing else, so tuning the sales voice can
// never move a patient call or a customer support answer.

const WRITE_TIMEOUT_MS = 20000;

function OutreachPrompts() {
  const toast = useToast();
  const { colorMode } = useColorMode();
  const mutedColor = colorMode === "light" ? "gray.600" : "gray.400";

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await cqAPI.get("/outreach/prompts?bid=");
        if (response?.type === "ok" && response.data) {
          setForm(response.data);
        } else {
          setLoadFailed(true);
        }
      } catch (e) {
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const response = await cqAPI.put("/outreach/prompts", form, WRITE_TIMEOUT_MS);
      if (response?.type === "ok") {
        setForm((f) => ({ ...f, ...response.data }));
        toast({ title: "Saved", status: "success", duration: 2000 });
      } else {
        toast({ title: "Not saved", description: response?.msg, status: "error", duration: 6000, isClosable: true });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box className="outreach-page page-pad" p={4}><Flex justify="center" py={10}><Spinner size="xl" color="blue.500" /></Flex></Box>;
  if (loadFailed || !form) {
    return (
      <Box className="outreach-page page-pad" p={4}>
        <Text color="red.400">The outreach prompts could not be loaded. Reload before editing — saving a blank
          form would replace the live configuration.</Text>
      </Box>
    );
  }

  return (
    <Box className="outreach-page page-pad" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Text fontSize="xl" fontWeight="bold">Outreach Prompts</Text>
        <Button colorScheme="teal" onClick={save} isLoading={saving}>Save</Button>
      </Flex>

      <Stack spacing={4} maxW="860px">
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="outreach-enabled" mb="0">Assistant answers replies automatically</FormLabel>
          <Switch id="outreach-enabled" isChecked={!!form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        </FormControl>
        <Text fontSize="xs" color={mutedColor} textAlign="left">
          Off means replies still thread into the Outreach Inbox and wait for a person; nothing answers on its own.
        </Text>

        <Flex gap={3} wrap="wrap">
          <FormControl width="200px">
            <FormLabel>LLM Provider</FormLabel>
            <Select value={form.llm_provider || "grok"} onChange={(e) => setForm({ ...form, llm_provider: e.target.value })}>
              <option value="grok">Grok</option>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
            </Select>
          </FormControl>
          <FormControl width="200px">
            <FormLabel>Model Size</FormLabel>
            <Select value={form.model_size || "large"} onChange={(e) => setForm({ ...form, model_size: e.target.value })}>
              <option value="large">Large</option>
              <option value="small">Small</option>
            </Select>
          </FormControl>
        </Flex>

        <FormControl>
          <FormLabel>System prompt</FormLabel>
          <Textarea rows={8} value={form.system_prompt || ""} placeholder="Leave empty to use the built-in sales persona."
            onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} />
        </FormControl>

        <FormControl>
          <FormLabel>Product FAQ (the ONLY thing the assistant may claim about Viva)</FormLabel>
          <Textarea rows={12} value={form.product_faq || ""}
            placeholder="Pricing, integrations, features — plain text. If it is not in here, the assistant says a teammate will follow up."
            onChange={(e) => setForm({ ...form, product_faq: e.target.value })} />
        </FormControl>

        <FormControl>
          <FormLabel>Unsure reply</FormLabel>
          <Input value={form.unsure_reply || ""} placeholder="Sent instead of a guess."
            onChange={(e) => setForm({ ...form, unsure_reply: e.target.value })} />
        </FormControl>
      </Stack>
    </Box>
  );
}

export default OutreachPrompts;
